import { useState, useContext, useEffect } from "react";
import { Cable } from "../model/viewModel/cable";
import { Asset } from "../model/viewModel/asset";
import { Label, Input, InputGroup, InputGroupAddon, Button } from "reactstrap";
import { FormContext } from "../context/FormContext";
import { ToolContext } from "../context/ToolContext";
import { useSelector, useDispatch } from "react-redux";
import { getAssetById } from "../utils/networkFunctions";
import EATooltip from "./EATooltip";
import {
  clearResults,
  findAll,
  updateCable,
  updateCableArray,
  findCable,
} from "../app/networkSlice";
import { getResultProperties } from "../utils/referenceFunctions";
import { RootState, store } from "../app/store";
import { endBatchAction, endSkipAction, startBatchAction, startSkipAction } from "../app/undoable";

interface ILengthEvent {
  value?: string;
  target?: { value: string };
  preventDefault(): void;
}

interface CableLengthEditorProps {
  cable: Cable;
  clearErrors: () => void;
}

const CableLengthEditor = ({ cable, clearErrors }: CableLengthEditorProps) => {
  const { formState, dispatch } = useContext(FormContext);
  const { clientSettings } = formState;
  const poleTermLengthEnabled = clientSettings.features.PoleTermLengthEnabled;
  const { toolState, setToolState } = useContext(ToolContext);
  const { clickedAsset } = toolState;
  const activeCable = useSelector((state: RootState) =>
    findCable(state, (clickedAsset as Asset)?.id),
  );
  const { cableGroup, length, overrideLength } = activeCable ? activeCable : cable;

  const allAssets = useSelector((state: RootState) => findAll(state));

  const dispatchRedux = useDispatch();

  const [enableOverrideLength, setEnableOverrideLength] = useState<boolean>(overrideLength > 0);
  const [editedOverrideLength, setEditedOverrideLength] = useState<string>(String(overrideLength));

  useEffect(() => {
    setEnableOverrideLength(overrideLength > 0);
    setEditedOverrideLength(String(overrideLength));
  }, [overrideLength]);

  const changeInputValue = (e: ILengthEvent) => {
    const value = e.value !== undefined ? e.value : e.target?.value;

    store.dispatch(startBatchAction());
    try {
      dispatch({
        form: "cableProperties",
        field: "overrideLength",
        value: value,
        type: "UPDATE_FIELD",
      });
      if (clickedAsset && (clickedAsset as Asset).styles.type === "cable") {
        const clickedCable = clickedAsset as Cable;
        updateGroundTypeOverrides(clickedCable, value);
        clearErrors();
        dispatchRedux(
          updateCable({ id: clickedCable.id, name: "overrideLength", value: value ? value : null }),
        );
      }
    } finally {
      store.dispatch(endBatchAction());
    }
  };

  const updateGroundTypeOverrides = (clickedCable: Cable, newLength: string | undefined): void => {
    if (!clickedCable.groundTypeOverrides) {
      return;
    }

    if (clickedCable.groundTypeOverrides.length === 1) {
      dispatch({
        form: "cableProperties",
        field: "groundTypeOverrides",
        value: [
          ...clickedCable.groundTypeOverrides.map((p, i) =>
            i === 0 ? { ...p, length: newLength } : p,
          ),
        ],
        type: "UPDATE_FIELD",
      });
      dispatchRedux(
        updateCableArray({
          id: clickedCable.id,
          arrayProperty: "groundTypeOverrides",
          index: 0,
          name: "length",
          value: newLength,
        }),
      );
    }
  };

  const hideResults = (): void => {
    const _toolState = toolState;
    _toolState.showResults = false;
    setToolState(_toolState);
    dispatchRedux(clearResults(getResultProperties(clientSettings)));
  };

  const handleChangeOverrideLength = (e: ILengthEvent): void => {
    setEditedOverrideLength(e.target?.value ?? "");
  };

  const handleBlurOverrideLength = (e: ILengthEvent): void => {
    const value = e?.target?.value || "0";
    const formattedValue = formatOverrideLengthPrecision(value);
    const numericValue = parseFloat(formattedValue);

    if (numericValue <= 0) {
      store.dispatch(startSkipAction());
      try {
        hideResults();
        toggleOverrideLength();
      } finally {
        store.dispatch(endSkipAction());
      }
    } else {
      setEditedOverrideLength(formatOverrideLengthPrecision(value));

      if (formattedValue !== String(overrideLength)) {
        store.dispatch(startSkipAction());
        try {
          hideResults();
          changeInputValue({
            value: formattedValue,
          } as ILengthEvent);
        } finally {
          store.dispatch(endSkipAction());
        }
      }
    }
  };

  const toggleOverrideLength = (): void => {
    hideResults();
    const enabled = !enableOverrideLength;
    setEnableOverrideLength(enabled);
    store.dispatch(startBatchAction());
    try {
      if (enabled) {
        setEditedOverrideLength(formatOverrideLengthPrecision(length));

        changeInputValue({
          value: String(length),
        } as ILengthEvent);
      } else {
        changeInputValue({
          value: "",
        } as ILengthEvent);

        updateGroundTypeOverrides(clickedAsset as Cable, String(length));
      }
    } finally {
      store.dispatch(endBatchAction());
    }
  };

  // true for a service underground cable which is connected via a pole to a mains overhead line
  const mayHaveServiceConnectionLength = (): boolean => {
    if (!clickedAsset || !cable || cableGroup !== "service-underground") {
      return false;
    }

    const startAsset = getAssetById(allAssets, cable.startAssetId);
    const endAsset = getAssetById(allAssets, cable.endAssetId);

    return [startAsset, endAsset].some((asset: any) => {
      if (asset?.pole?.enabled) {
        return allAssets.some((assetType) => {
          if (assetType.type === "cables") {
            const connectedAsset = assetType.asset as Cable;
            if (
              connectedAsset.startAssetId === asset.id ||
              connectedAsset.endAssetId === asset.id
            ) {
              if (connectedAsset.cableGroup === "mains-overhead") {
                return true;
              }
            }
          }
          return false;
        });
      }
      return false;
    });
  };

  const hasPoleTermLength = (): boolean => {
    if (!clickedAsset || !cable || !cableGroup.includes("underground")) {
      return false;
    }

    const start: any = getAssetById(allAssets, cable.startAssetId);
    const end: any = getAssetById(allAssets, cable.endAssetId);

    let total = 0;

    if (start?.mounting === "pmt") {
      total += parseFloat(start.poleTermLength);
    } else if (start?.pole?.enabled) {
      total += parseFloat(start.pole.poleTermLength ?? 0);
    }

    if (end?.mounting === "pmt") {
      total += parseFloat(end.poleTermLength);
    } else if (end?.pole?.enabled) {
      total += parseFloat(end.pole.poleTermLength ?? 0);
    }

    return total > 0;
  };

  const additionalLengthDescriptions = (): string[] => {
    const descriptions = [];
    if (hasPoleTermLength()) {
      descriptions.push("pole term(s)");
    }
    if (mayHaveServiceConnectionLength()) {
      descriptions.push("underground service connecting to overhead line");
    }
    return descriptions;
  };

  const additionalLengthPopupText = (): string => {
    const reasons = additionalLengthDescriptions();
    if (!reasons.length) {
      return "";
    }
    return "Additional length may be added for " + reasons.join(" and ");
  };

  const shouldShowAdditionalLengthFlag = (): boolean =>
    poleTermLengthEnabled && additionalLengthPopupText();

  const formatOverrideLengthPrecision = (value: string | number): string => {
    var parsedValue = parseFloat(String(value));
    if (parsedValue > 0 && parsedValue < 0.01) {
      return parsedValue.toFixed(10).replace(/\.?0+$/, "");
    }
    return parsedValue.toFixed(2);
  };

  return (
    <>
      <Label for="length">Length (m)</Label>
      <InputGroup>
        {enableOverrideLength ? (
          <Input
            type="number"
            id="overrideLength"
            name="overrideLength"
            className="w-50 no-spinner"
            placeholder="0"
            value={editedOverrideLength}
            onChange={handleChangeOverrideLength}
            onBlur={handleBlurOverrideLength}
            autoFocus
          />
        ) : (
          <Input
            type="number"
            className="no-spinner"
            id="length"
            name="length"
            placeholder="0"
            value={parseFloat(String(length)).toFixed(2)}
            onClick={toggleOverrideLength}
            readOnly
          />
        )}
        {shouldShowAdditionalLengthFlag() && (
          <InputGroupAddon addonType="append">
            <>
              <EATooltip
                text={additionalLengthPopupText()}
                target="additional-length-icon"
                placement="top"
              />
              <span id="additional-length-icon" className="btn btn-dark">
                <i className={`icon-circle-dot`}></i>
              </span>
            </>
          </InputGroupAddon>
        )}
        <InputGroupAddon addonType="append">
          <Button color="dark" onClick={toggleOverrideLength}>
            <i className={`icon-${enableOverrideLength ? "lock" : "lock-open"}-solid`}></i>
          </Button>
        </InputGroupAddon>
      </InputGroup>
      <small>
        {enableOverrideLength
          ? "Manually input length"
          : length < 1
            ? "Awaiting route..."
            : "Calculated length"}
      </small>
    </>
  );
};

export default CableLengthEditor;
